import type { MyOrg } from '@carouselgg/auth/use-my-orgs'
import { isTimeTbd, type MyMatch } from '../../../api/matches'
import type { MyTeam, OrgSeason, StandingRow } from '../../../api/teams'
import { isOrgAdmin } from '../destinations'
import { matchPath, type NeedsYouItem, type NeedsYouPlace } from '../needsYou'
import type { UpcomingEntry } from '../upcoming'

/**
 * What the home page shows, worked out from what the dashboard already loads.
 *
 * Pure functions, so every rule here is testable without a DOM: who the next
 * match is against, how a week of fixtures and competition dates reads as one
 * row, which org a to-do belongs to.
 */

const DAY_MS = 24 * 60 * 60 * 1000

/** How far ahead the week row looks for dates in competitions you run. */
const WEEK_AHEAD_DAYS = 14

// ---------------------------------------------------------------- dates

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

/** "Today", "Tomorrow", "Mon · Nov 2". */
export function dayLabel(at: Date, now: Date): string {
  if (sameDay(at, now)) return 'Today'
  if (sameDay(at, new Date(now.getTime() + DAY_MS))) return 'Tomorrow'
  const weekday = at.toLocaleDateString(undefined, { weekday: 'short' })
  const date = at.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  return `${weekday} · ${date}`
}

export function timeLabel(at: Date): string {
  return at.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
}

// ---------------------------------------------------------------- matches

export interface MatchSide {
  teamID: string
  name: string
  logoUrl?: string
  record?: string
  mine: boolean
}

/**
 * The part of a match title that isn't the team names: "Week 8",
 * "Gold Ladder League, Week 1". Titles read "A vs B — Week 8" or
 * "A vs B · League, Week 1"; one with neither separator says nothing extra.
 */
export function matchSubtitle(title: string): string | undefined {
  for (const sep of [' — ', ' · ']) {
    const i = title.lastIndexOf(sep)
    if (i >= 0) return title.slice(i + sep.length).trim() || undefined
  }
  return undefined
}

function record(wins: number, losses: number): string | undefined {
  return wins + losses > 0 ? `${wins}–${losses}` : undefined
}

/**
 * Both sides of a fixture, the viewer's first. A side's record comes from the
 * viewer's own team list, or from a standings table the dashboard loaded for
 * that competition; with neither, it is left off rather than shown as 0–0.
 */
export function matchSides(
  match: MyMatch,
  teams: MyTeam[],
  standings: Record<string, StandingRow[]>,
): MatchSide[] {
  const rows = Object.values(standings).flat()
  const sides = match.teams.map((t): MatchSide => {
    const mine = teams.find((m) => m.id === t.team_id)
    const standing = rows.find((r) => r.team_name === t.team_name)
    return {
      teamID: t.team_id,
      name: t.team_name,
      logoUrl: t.team_logo || mine?.logo_url || undefined,
      record: mine ? record(mine.wins, mine.losses) : standing ? record(standing.wins, standing.losses) : undefined,
      mine: Boolean(mine),
    }
  })
  return sides.sort((a, b) => Number(b.mine) - Number(a.mine))
}

// ---------------------------------------------------------------- the week row

export type WeekCard =
  | {
      kind: 'match'
      id: string
      at: Date
      tbd: boolean
      live: boolean
      to: string
      sides: MatchSide[]
      game: string
      subtitle?: string
      host?: string
    }
  | {
      kind: 'season-start'
      id: string
      at: Date
      to: string
      title: string
      detail: string
      logoUrl?: string
    }

/**
 * One row for the next fortnight: the matches you play, and the starts of
 * competitions you run. Sorted by date, matches with no time yet at the end.
 */
export function weekCards(input: {
  upcoming: UpcomingEntry[]
  teams: MyTeam[]
  standings: Record<string, StandingRow[]>
  seasons: OrgSeason[]
  orgs: MyOrg[]
  now: Date
}): WeekCard[] {
  const { upcoming, teams, standings, seasons, orgs, now } = input
  const horizon = now.getTime() + WEEK_AHEAD_DAYS * DAY_MS

  const matches: WeekCard[] = upcoming.map(({ match }) => ({
    kind: 'match',
    id: match.id,
    at: new Date(match.starts_at),
    tbd: isTimeTbd(match),
    live: match.status === 'live',
    to: matchPath(match) ?? '/compete',
    sides: matchSides(match, teams, standings),
    game: match.game_name,
    subtitle: matchSubtitle(match.title),
    host: match.organization?.name,
  }))

  const starts: WeekCard[] = seasons.flatMap((s): WeekCard[] => {
    const org = orgs.find((o) => o.id === s.organization_id)
    if (!org || !isOrgAdmin(org.role) || !s.starts_at) return []
    const at = new Date(s.starts_at)
    if (Number.isNaN(at.getTime()) || at.getTime() <= now.getTime() || at.getTime() > horizon) return []
    const teamsLine = `${s.registered_teams} team${s.registered_teams === 1 ? '' : 's'} so far`
    return [{
      kind: 'season-start',
      id: `start-${s.id}`,
      at,
      to: `/org/${org.slug}/manage/competitions/${s.id}`,
      title: s.name,
      detail: `starts · ${teamsLine}`,
      logoUrl: org.logoUrl,
    }]
  })

  return [...matches, ...starts].sort((a, b) => {
    const atb = a.kind === 'match' && a.tbd
    const btb = b.kind === 'match' && b.tbd
    if (atb !== btb) return atb ? 1 : -1
    return a.at.getTime() - b.at.getTime()
  })
}

// ---------------------------------------------------------------- to do

export interface TodoGroup {
  key: string
  /** Null for the viewer's own account. */
  place: NeedsYouPlace | null
  items: NeedsYouItem[]
}

/**
 * The to-do list, grouped under the team or org each item belongs to, in the
 * order the most urgent item of each group ranks. Items about the viewer's own
 * account (trust) gather under one "Your account" group.
 */
export function todoGroups(items: NeedsYouItem[]): TodoGroup[] {
  const groups = new Map<string, TodoGroup>()
  for (const item of items) {
    const key = item.place?.key ?? 'account'
    const group = groups.get(key)
    if (group) group.items.push(item)
    else groups.set(key, { key, place: item.place ?? null, items: [item] })
  }
  return [...groups.values()]
}

// ---------------------------------------------------------------- orgs you run

export interface OrgPanel {
  org: MyOrg
  kind: 'league' | 'club'
  /** Headline numbers. Only ones the dashboard actually loaded. */
  figures: { label: string; value: number }[]
  /** The most urgent to-do filed under this org, if any. */
  next?: NeedsYouItem
  manageTo: string
}

const RUNNING: OrgSeason['status'][] = ['active', 'playoffs']

/** Every org the viewer runs, busiest first. */
export function orgPanels(
  orgs: MyOrg[],
  seasons: OrgSeason[],
  todo: NeedsYouItem[],
  /** Applications waiting to join a club's teams, per club. */
  waitingByOrg: Record<string, number> = {},
): OrgPanel[] {
  const panels = orgs
    .filter((o) => isOrgAdmin(o.role))
    .map((org): OrgPanel => {
      const own = seasons.filter((s) => s.organization_id === org.id)
      const isLeague = org.type === 'league'
      const figures = isLeague
        ? [
            { label: 'Results owed', value: own.reduce((n, s) => n + s.overdue_results, 0) },
            { label: 'Running', value: own.filter((s) => RUNNING.includes(s.status)).length },
            { label: 'Open', value: own.filter((s) => s.status === 'open').length },
          ]
        : [{ label: 'Waiting to join', value: waitingByOrg[org.id] ?? 0 }]
      return {
        org,
        kind: isLeague ? 'league' : 'club',
        figures,
        next: todo.find((t) => t.place?.key === org.id || t.place?.parentKey === org.id),
        manageTo: `/org/${org.slug}/manage`,
      }
    })
  const weight = (p: OrgPanel) => (p.next ? 1000 : 0) + p.figures.reduce((n, f) => n + f.value, 0)
  return panels.sort((a, b) => weight(b) - weight(a))
}

/** A league's place for grouping its to-dos, so they line up with its panel. */
export function leaguePlace(org: MyOrg | undefined): NeedsYouPlace | undefined {
  if (!org) return undefined
  return {
    key: org.id,
    name: org.name,
    logoUrl: org.logoUrl,
    color: org.brandColor,
    role: isOrgAdmin(org.role) ? 'League you run' : 'League',
  }
}
